import React, { useState, useEffect } from 'react';
import { Row, Col, Table, Button, Badge, Alert, Modal, Form, InputGroup, Dropdown, Pagination } from 'react-bootstrap';
import axios from 'axios';

interface User {
  _id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  userType: 'free' | 'executive' | 'admin';
  rewardBalance: number;
  referralCode: string;
  createdAt: string;
  isActive: boolean;
  lastLogin?: string;
  totalEarnings?: number;
  tasksCompleted?: number;
  lastUpgradeDate?: string;
  upgradeHistory?: Array<{
    fromType: string;
    toType: string;
    upgradedAt: string;
    transactionId?: string;
  }>;
  referralInfo?: {
    referredBy: {
      _id: string;
      name: string;
      email: string;
      referralCode: string;
    };
    usedReferralCode: string;
    referredAt: string;
    status: string;
    rewards: {
      referrerReward: number;
      refereeReward: number;
    };
  };
  referralStats?: {
    totalReferrals: number;
    completedReferrals: number;
    rewardedReferrals: number;
    totalReferrerRewards: number;
    totalRefereeRewards: number;
  };
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'admin'>('all');
  const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'free' | 'executive' | 'admin'>('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginatedUsers, setPaginatedUsers] = useState<User[]>([]);
    // Filtered users
    const filteredUsers = users.filter(user => {
      const searchMatch =
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      const roleMatch = roleFilter === 'all' || user.role === roleFilter;
      const typeMatch = userTypeFilter === 'all' || user.userType === userTypeFilter;
      return searchMatch && roleMatch && typeMatch;
    });

    useEffect(() => {
      setCurrentPage(1);
    }, [searchTerm, roleFilter, userTypeFilter, users.length]);

    useEffect(() => {
      const startIdx = (currentPage - 1) * pageSize;
      const endIdx = startIdx + pageSize;
      setPaginatedUsers(filteredUsers.slice(startIdx, endIdx));
    }, [filteredUsers, currentPage, pageSize]);
  
  // Modal-specific error and success states
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  
  console.log('UserManagement - API_BASE_URL:', API_BASE_URL);

  // Form data for adding/editing users
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user' as 'user' | 'admin',
    userType: 'free' as 'free' | 'executive' | 'admin',
    rewardBalance: 0
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      const currentToken = localStorage.getItem('token');
      
      console.log('Loading users with token:', currentToken ? `Token: ${currentToken.substring(0, 50)}...` : 'No token');
      console.log('API URL:', `${API_BASE_URL}/admin/users`);
      
      // Always try real API call first
      const response = await axios.get(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });

      if (response.data.success) {
        // Map backend user data to frontend interface
        const mappedUsers: User[] = response.data.users.map((user: any) => ({
          _id: user._id || user.id,
          name: user.name,
          email: user.email,
          role: user.role,
          userType: user.userType || (user.role === 'admin' ? 'admin' : 'free'),
          rewardBalance: user.rewardBalance || 0,
          referralCode: user.referralCode || generateReferralCode(user.name),
          createdAt: user.createdAt,
          isActive: user.isActive !== undefined ? user.isActive : true,
          lastLogin: user.lastLogin,
          totalEarnings: user.totalEarnings || 0,
          tasksCompleted: user.tasksCompleted || 0,
          lastUpgradeDate: user.lastUpgradeDate,
          upgradeHistory: user.upgradeHistory || []
        }));

        setUsers(mappedUsers);
      } else {
        setError('Failed to load users: ' + response.data.message);
      }
      setLoading(false);
    } catch (error: any) {
      console.error('Error loading users:', error);
      console.error('Error details:', error.response?.data);
      console.error('Status code:', error.response?.status);
      
      // Only use mock data as last resort if using mock token
      const currentToken = localStorage.getItem('token');
      if (currentToken === 'mock-admin-token-12345') {
        console.log('Falling back to mock data for demo purposes');
        const mockUsers: User[] = [
          {
            _id: 'admin-1',
            name: 'Admin User',
            email: 'admin@networqpay.com',
            role: 'admin',
            userType: 'admin',
            rewardBalance: 0,
            referralCode: 'ADMIN001',
            createdAt: '2024-01-01T00:00:00Z',
            isActive: true,
            lastLogin: new Date().toISOString(),
            totalEarnings: 0,
            tasksCompleted: 0
          }
        ];
        setUsers(mockUsers);
        setLoading(false);
        return;
      }
      
      setError(error.response?.data?.message || 'Failed to connect to server. Please ensure you are logged in with a real admin account.');
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('UserManagement - Current user from localStorage:', localStorage.getItem('token') ? 'Token exists' : 'No token');
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');
    setModalSuccess('');
    setModalLoading(true);

    try {
      const currentToken = localStorage.getItem('token');
      
      // Always try real API call first
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        rewardBalance: formData.rewardBalance
      };

      const response = await axios.post(`${API_BASE_URL}/admin/users`, userData, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setModalSuccess('User added successfully to database!');
        // Close modal after a short delay to show success message
        setTimeout(() => {
          setShowAddModal(false);
          resetForm();
          setModalSuccess('');
          setSuccess('User added successfully to database!');
        }, 1500);
        // Reload users list from database
        loadUsers();
      } else {
        setModalError(response.data.message || 'Failed to add user to database');
      }
    } catch (error: any) {
      console.error('Error adding user:', error);
      
      // Handle validation errors with details
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => 
          `${err.path ? err.path.charAt(0).toUpperCase() + err.path.slice(1) : ''}: ${err.msg}`
        );
        setModalError(`Validation failed:\n• ${errorMessages.join('\n• ')}`);
        return;
      }
      
      // Only use mock for demo mode
      const currentToken = localStorage.getItem('token');
      if (currentToken === 'mock-admin-token-12345') {
        console.log('Adding user to mock data for demo');
        const newUser: User = {
          _id: Date.now().toString(),
          name: formData.name,
          email: formData.email,
          role: formData.role,
          userType: formData.userType,
          rewardBalance: formData.rewardBalance,
          referralCode: generateReferralCode(formData.name),
          createdAt: new Date().toISOString(),
          isActive: true,
          totalEarnings: 0,
          tasksCompleted: 0
        };

        setUsers(prev => [...prev, newUser]);
        setModalSuccess('User added successfully (demo mode)!');
        setTimeout(() => {
          setShowAddModal(false);
          resetForm();
          setModalSuccess('');
          setSuccess('User added successfully (demo mode)!');
        }, 1500);
        return;
      }
      
      setModalError(error.response?.data?.message || 'Failed to add user to database. Please ensure you are logged in with a real admin account.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');
    setModalSuccess('');
    setModalLoading(true);

    if (!editingUser) return;

    try {
      const currentToken = localStorage.getItem('token');
      
      // Handle mock authentication for demo
      if (currentToken === 'mock-admin-token-12345') {
        const updatedUser = {
          ...editingUser,
          name: formData.name,
          email: formData.email,
          role: formData.role,
          rewardBalance: formData.rewardBalance
        };

        setUsers(prev => prev.map(u => u._id === editingUser._id ? updatedUser : u));
        setModalSuccess('User updated successfully!');
        setTimeout(() => {
          setShowEditModal(false);
          setEditingUser(null);
          resetForm();
          setModalSuccess('');
          setSuccess('User updated successfully!');
        }, 1500);
        return;
      }

      // Real API call for production
      const userData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        rewardBalance: formData.rewardBalance
      };

      const response = await axios.put(`${API_BASE_URL}/admin/users/${editingUser._id}`, userData, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setModalSuccess('User updated successfully!');
        setTimeout(() => {
          setShowEditModal(false);
          setEditingUser(null);
          resetForm();
          setModalSuccess('');
          setSuccess('User updated successfully!');
        }, 1500);
        // Reload users list
        loadUsers();
      } else {
        setModalError(response.data.message || 'Failed to update user');
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      
      // Handle validation errors with details
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => 
          `${err.path ? err.path.charAt(0).toUpperCase() + err.path.slice(1) : ''}: ${err.msg}`
        );
        setModalError(`Validation failed:\n• ${errorMessages.join('\n• ')}`);
      } else {
        setModalError(error.response?.data?.message || 'Failed to update user');
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        const currentToken = localStorage.getItem('token');
        
        // Handle mock authentication for demo
        if (currentToken === 'mock-admin-token-12345') {
          // Don't allow deleting the main admin in demo mode
          const userToDelete = users.find(u => u._id === userId);
          if (userToDelete?.email === 'admin@networqpay.com') {
            setError('Cannot delete the main admin account');
            return;
          }
          
          setUsers(prev => prev.filter(u => u._id !== userId));
          setSuccess('User deleted successfully!');
          return;
        }

        // Real API call for production
        const response = await axios.delete(`${API_BASE_URL}/admin/users/${userId}`, {
          headers: {
            'Authorization': `Bearer ${currentToken}`
          }
        });

        if (response.data.success) {
          setSuccess('User deleted successfully!');
          // Reload users list
          loadUsers();
        } else {
          setError(response.data.message || 'Failed to delete user');
        }
      } catch (error: any) {
        console.error('Error deleting user:', error);
        setError(error.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const toggleUserStatus = async (userId: string) => {
    const user = users.find(u => u._id === userId);
    if (!user) return;
    
    const action = user.isActive ? 'deactivate' : 'activate';
    const confirmMessage = `Are you sure you want to ${action} ${user.name}? ${
      user.isActive ? 'This will prevent them from accessing the platform.' : 'This will restore their access to the platform.'
    }`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const currentToken = localStorage.getItem('token');
      
      // Handle mock authentication for demo
      if (currentToken === 'mock-admin-token-12345') {
        setUsers(prev => prev.map(u => 
          u._id === userId ? { ...u, isActive: !u.isActive } : u
        ));
        setSuccess(`User ${action}d successfully!`);
        return;
      }

      // Real API call for production
      const response = await axios.patch(`${API_BASE_URL}/admin/users/${userId}/toggle-status`, {}, {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });

      if (response.data.success) {
        setSuccess(response.data.message || 'User status updated successfully!');
        // Reload users list
        loadUsers();
      } else {
        setError(response.data.message || 'Failed to update user status');
      }
    } catch (error: any) {
      console.error('Error updating user status:', error);
      setError(error.response?.data?.message || 'Failed to update user status');
    }
  };

  const generateReferralCode = (name: string): string => {
    return (name.toUpperCase().replace(/\s/g, '').substring(0, 4) + 
           Math.random().toString(36).substring(2, 6).toUpperCase());
  };

  const getUserTypeBadge = (userType: string) => {
    const variants: { [key: string]: string } = {
      free: 'secondary',
      pro: 'warning',
      admin: 'danger'
    };
    const type = userType || 'free';
    return <Badge bg={variants[type] || 'secondary'}>{type.toUpperCase()}</Badge>;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'user',
      userType: 'free',
      rewardBalance: 0
    });
    // Clear modal states
    setModalError('');
    setModalSuccess('');
    setModalLoading(false);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      userType: user.userType,
      rewardBalance: user.rewardBalance
    });
    setShowEditModal(true);
  };

  

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h1 className="h3 mb-1">User Management</h1>
          <p className="text-muted mb-0">Manage users, roles, and permissions</p>
        </Col>
      </Row>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError('')}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess('')}>
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col xl={3} md={6} className="mb-3">
          <div className="admin-stats-card">
            <i className="fas fa-users admin-stats-icon"></i>
            <div className="admin-stats-number">{users.length}</div>
            <div className="admin-stats-label">Total Users</div>
          </div>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <div className="admin-stats-card success">
            <i className="fas fa-user-check admin-stats-icon"></i>
            <div className="admin-stats-number">{users.filter(u => u.isActive).length}</div>
            <div className="admin-stats-label">Active Users</div>
          </div>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <div className="admin-stats-card warning">
            <i className="fas fa-user-shield admin-stats-icon"></i>
            <div className="admin-stats-number">{users.filter(u => u.role === 'admin').length}</div>
            <div className="admin-stats-label">Administrators</div>
          </div>
        </Col>
        <Col xl={3} md={6} className="mb-3">
          <div className="admin-stats-card info">
            <i className="fas fa-coins admin-stats-icon"></i>
            <div className="admin-stats-number">₹{users.reduce((sum, u) => sum + u.rewardBalance, 0).toFixed(0)}</div>
            <div className="admin-stats-label">Total Balance</div>
          </div>
        </Col>
      </Row>

      {/* User Management Card */}
      <div className="admin-card">
        <div className="admin-card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-users me-2"></i>
            Users ({filteredUsers.length})
          </h5>
          <Button 
            variant="primary" 
            onClick={() => setShowAddModal(true)}
            className="d-flex align-items-center"
          >
            <i className="fas fa-plus me-2"></i>
            Add User
          </Button>
        </div>

        {/* Filters */}
        <div className="admin-card-body border-bottom">
          <Row>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={4}>
              <Form.Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as 'all' | 'user' | 'admin')}
              >
                <option value="all">All Roles</option>
                <option value="user">Users Only</option>
                <option value="admin">Admins Only</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Select
                value={userTypeFilter}
                onChange={(e) => setUserTypeFilter(e.target.value as 'all' | 'free' | 'executive' | 'admin')}
              >
                <option value="all">All User Types</option>
                <option value="free">Free Users</option>
                <option value="executive">Executive Users</option>
                <option value="admin">Admin Users</option>
              </Form.Select>
            </Col>
          </Row>
        </div>

        {/* Users Table */}
        <div className="admin-card-body p-0">
          <Table responsive className="admin-table mb-0">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>User Type</th>
                <th>Referral Info</th>
                <th>Balance</th>
                <th>Earnings</th>
                <th>Tasks</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => (
                <tr key={user._id}>
                  {/* ...existing code for user row... */}
                  <td>
                    <div className="d-flex align-items-center">
                      <div 
                        className="admin-user-avatar me-2" 
                        style={{
                          width: '36px', 
                          height: '36px', 
                          fontSize: '0.8rem',
                          background: user.role === 'admin' 
                            ? 'linear-gradient(135deg, #dc3545, #c82333)' 
                            : 'linear-gradient(135deg, #007bff, #0056b3)'
                        }}
                      >
                        <i className={user.role === 'admin' ? 'fas fa-user-shield' : 'fas fa-user'}></i>
                      </div>
                      <div>
                        <strong>{user.name}</strong>
                        <div className="small text-muted">{user.referralCode}</div>
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <Badge bg={user.role === 'admin' ? 'danger' : 'primary'}>
                      {user.role.toUpperCase()}
                    </Badge>
                  </td>
                  <td>
                    {getUserTypeBadge(user.userType)}
                    {user.lastUpgradeDate && (
                      <div className="small text-muted mt-1">
                        Upgraded: {new Date(user.lastUpgradeDate).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td>
                    {user.referralInfo ? (
                      <div>
                        <div className="small">
                          <i className="fas fa-user-plus text-success me-1"></i>
                          <strong>Referred by:</strong>
                        </div>
                        <div className="small text-primary">
                          {user.referralInfo.referredBy.name}
                        </div>
                        <div className="small text-muted">
                          Code: {user.referralInfo.usedReferralCode}
                        </div>
                      </div>
                    ) : (
                      <div className="small text-muted">
                        <i className="fas fa-user me-1"></i>
                        Direct signup
                      </div>
                    )}
                    {user.referralStats && user.referralStats.totalReferrals > 0 && (
                      <div className="mt-2">
                        <Badge bg="info" className="small">
                          Referred: {user.referralStats.totalReferrals}
                        </Badge>
                      </div>
                    )}
                  </td>
                  <td>
                    <strong className="text-success">₹{user.rewardBalance.toFixed(2)}</strong>
                  </td>
                  <td>
                    <span className="text-info">₹{user.totalEarnings?.toFixed(2) || '0.00'}</span>
                  </td>
                  <td>
                    <Badge bg="secondary">{user.tasksCompleted || 0}</Badge>
                  </td>
                  <td>
                    <Badge bg={user.isActive ? 'success' : 'warning'}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td>
                    <small className="text-muted">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                    </small>
                  </td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" size="sm">
                        <i className="fas fa-ellipsis-v"></i>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => openEditModal(user)}>
                          <i className="fas fa-edit me-2"></i>
                          Edit User
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => toggleUserStatus(user._id)}>
                          <i className={`fas ${user.isActive ? 'fa-ban' : 'fa-check'} me-2`}></i>
                          {user.isActive ? 'Deactivate' : 'Activate'}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item 
                          onClick={() => handleDeleteUser(user._id)}
                          className="text-danger"
                          disabled={user.email === 'admin@networqpay.com'}
                        >
                          <i className="fas fa-trash me-2"></i>
                          Delete User
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {/* Pagination Controls (below table) */}
          <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
            <div>
              <Form.Select
                style={{ width: 120, display: 'inline-block' }}
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                size="sm"
              >
                {[5, 10, 20, 50, 100].map(size => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </Form.Select>
            </div>
            <div>
              <Pagination className="mb-0">
                <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                {(() => {
                  const totalPages = Math.ceil(filteredUsers.length / pageSize);
                  let start = Math.max(1, currentPage - 2);
                  let end = Math.min(totalPages, currentPage + 2);
                  if (end - start < 4) {
                    if (start === 1) end = Math.min(totalPages, start + 4);
                    if (end === totalPages) start = Math.max(1, end - 4);
                  }
                  return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(page => (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Pagination.Item>
                  ));
                })()}
                <Pagination.Next onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredUsers.length / pageSize), p + 1))} disabled={currentPage === Math.ceil(filteredUsers.length / pageSize) || filteredUsers.length === 0} />
                <Pagination.Last onClick={() => setCurrentPage(Math.ceil(filteredUsers.length / pageSize))} disabled={currentPage === Math.ceil(filteredUsers.length / pageSize) || filteredUsers.length === 0} />
              </Pagination>
            </div>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-5">
              <i className="fas fa-users fa-3x text-muted mb-3"></i>
              <h5 className="text-muted">No users found</h5>
              <p className="text-muted mb-3">
                {searchTerm || roleFilter !== 'all' || userTypeFilter !== 'all'
                  ? 'Try adjusting your filters to see more results.'
                  : 'Get started by adding your first user.'
                }
              </p>
              {(!searchTerm && roleFilter === 'all' && userTypeFilter === 'all') && (
                <Button variant="primary" onClick={() => setShowAddModal(true)}>
                  <i className="fas fa-plus me-2"></i>
                  Add First User
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-user-plus me-2"></i>
            Add New User
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddUser}>
          <Modal.Body>
            {/* Modal Error Alert */}
            {modalError && (
              <Alert variant="danger" className="mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <div style={{ whiteSpace: 'pre-line' }}>{modalError}</div>
              </Alert>
            )}
            
            {/* Modal Success Alert */}
            {modalSuccess && (
              <Alert variant="success" className="mb-3">
                <i className="fas fa-check-circle me-2"></i>
                {modalSuccess}
              </Alert>
            )}
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                    placeholder="Enter full name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                    placeholder="Enter email address"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({...prev, password: e.target.value}))}
                    placeholder="Enter password"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({...prev, role: e.target.value as 'user' | 'admin'}))}
                  >
                    <option value="user">User</option>
                    <option value="admin">Administrator</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>User Type</Form.Label>
                  <Form.Select
                    value={formData.userType}
                    onChange={(e) => setFormData(prev => ({...prev, userType: e.target.value as 'free' | 'executive' | 'admin'}))}
                  >
                    <option value="free">Free</option>
                    <option value="executive">Executive</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    User's subscription type (Free, Executive, or Admin)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Initial Reward Balance</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.rewardBalance}
                  onChange={(e) => setFormData(prev => ({...prev, rewardBalance: parseFloat(e.target.value) || 0}))}
                  placeholder="0.00"
                />
              </InputGroup>
              <Form.Text className="text-muted">
                Optional: Set an initial balance for the user
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => {
                resetForm();
                setShowAddModal(false);
              }}
              disabled={modalLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={modalLoading}
            >
              {modalLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Adding User...
                </>
              ) : (
                <>
                  <i className="fas fa-plus me-2"></i>
                  Add User
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-edit me-2"></i>
            Edit User: {editingUser?.name}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditUser}>
          <Modal.Body>
            {/* Modal Error Alert */}
            {modalError && (
              <Alert variant="danger" className="mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <div style={{ whiteSpace: 'pre-line' }}>{modalError}</div>
              </Alert>
            )}
            
            {/* Modal Success Alert */}
            {modalSuccess && (
              <Alert variant="success" className="mb-3">
                <i className="fas fa-check-circle me-2"></i>
                {modalSuccess}
              </Alert>
            )}
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                    placeholder="Enter full name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                    placeholder="Enter email address"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({...prev, role: e.target.value as 'user' | 'admin'}))}
                    disabled={editingUser?.email === 'admin@networqpay.com'}
                  >
                    <option value="user">User</option>
                    <option value="admin">Administrator</option>
                  </Form.Select>
                  {editingUser?.email === 'admin@networqpay.com' && (
                    <Form.Text className="text-muted">
                      Cannot change role of main admin account
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Reward Balance</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>₹</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.rewardBalance}
                      onChange={(e) => setFormData(prev => ({...prev, rewardBalance: parseFloat(e.target.value) || 0}))}
                      placeholder="0.00"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>User Type</Form.Label>
                  <Form.Select
                    value={formData.userType}
                    onChange={(e) => setFormData(prev => ({...prev, userType: e.target.value as 'free' | 'executive' | 'admin'}))}
                    disabled={editingUser?.email === 'admin@networqpay.com'}
                  >
                    <option value="free">Free</option>
                    <option value="executive">Executive</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                  {editingUser?.email === 'admin@networqpay.com' && (
                    <Form.Text className="text-muted">
                      Cannot change type of main admin account
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => {
                resetForm();
                setShowEditModal(false);
                setEditingUser(null);
              }}
              disabled={modalLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={modalLoading}
            >
              {modalLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Saving Changes...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Save Changes
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default UserManagement;